import { useState, useEffect, useCallback, useRef } from 'react';
import { getPosthog } from '@/lib/analytics';
import { deriveRoleFlags } from '@/lib/auth-roles';
import { getProfileByUserId, type Profile } from '@/services/profiles.service';
import {
  getMembershipByUserId,
  getOrganizationById,
  type Organization,
  type OrganizationMember,
} from '@/services/organizations.service';
import { getConsumerById, type Consumer } from '@/services/consumers.service';
import {
  extractOrgContext,
  getCurrentSession,
  subscribeToAuthChanges,
  signInWithPassword as signInService,
  signUp as signUpService,
  signOut as signOutService,
  type AppRole,
  type OrgRole,
  type Session,
  type User,
} from '@/services/auth.service';

export type { Organization, OrganizationMember };

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  orgRole: OrgRole | null;
  organizationId: string | null;
  isLoading: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  isAtendente: boolean;
  isPrestador: boolean;
  organization: Organization | null;
  membership: OrganizationMember | null;
  needsOnboarding: boolean;
  accountDeactivated: boolean;
  consumer: Consumer | null;
  isConsumer: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    role: null,
    orgRole: null,
    organizationId: null,
    isLoading: true,
    isAdmin: false,
    isOwner: false,
    isAtendente: false,
    isPrestador: false,
    organization: null,
    membership: null,
    needsOnboarding: false,
    accountDeactivated: false,
    consumer: null,
    isConsumer: false,
  });

  const previousEmailConfirmedAt = useRef<string | null | undefined>(undefined);
  const currentOrgIdRef = useRef<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  const fetchUserData = useCallback(async (userId: string, session: Session | null) => {
    try {
      currentUserIdRef.current = userId;
      // Fetch profile + membership + consumer in parallel (minor perf improvement vs legacy sequential await).
      const [profile, memberData, consumer] = await Promise.all([
        getProfileByUserId(userId),
        getMembershipByUserId(userId),
        getConsumerById(userId),
      ]);

      const { orgId, orgRole } = extractOrgContext(session);

      // Consumer (área do cliente): identidade global, fora do modelo de orgs.
      // Não precisa de onboarding nem de org; o ProtectedRoute de consumer trata o acesso.
      if (consumer) {
        setState(prev => ({
          ...prev,
          profile,
          role: null,
          orgRole: null,
          organizationId: null,
          isAdmin: false,
          isOwner: false,
          isAtendente: false,
          isPrestador: false,
          organization: null,
          membership: null,
          needsOnboarding: false,
          accountDeactivated: false,
          consumer,
          isConsumer: true,
          isLoading: false,
        }));
        return;
      }

      // Membership inativa: bloqueia o acesso. A RLS já recusa dados da org;
      // aqui marcamos o estado para o ProtectedRoute redirecionar.
      if (memberData && memberData.ativo === false) {
        setState(prev => ({
          ...prev,
          profile,
          role: null,
          orgRole: null,
          organizationId: null,
          isAdmin: false,
          isOwner: false,
          isAtendente: false,
          isPrestador: false,
          organization: null,
          membership: memberData,
          needsOnboarding: false,
          accountDeactivated: true,
          consumer: null,
          isConsumer: false,
          isLoading: false,
        }));
        return;
      }

      let organization: Organization | null = null;
      let membership: OrganizationMember | null = null;
      let needsOnboarding = false;
      let role: AppRole | null = null;
      let finalOrgRole: OrgRole | null = orgRole;

      if (memberData) {
        membership = memberData;
        role = memberData.role;

        if (!finalOrgRole) {
          finalOrgRole = memberData.role as OrgRole;
        }

        organization = await getOrganizationById(memberData.organization_id);
        currentOrgIdRef.current = memberData.organization_id;
      } else {
        needsOnboarding = true;
      }

      setState(prev => ({
        ...prev,
        profile,
        role,
        orgRole: finalOrgRole,
        organizationId: orgId || membership?.organization_id || null,
        ...deriveRoleFlags(role, finalOrgRole),
        organization,
        membership,
        needsOnboarding,
        accountDeactivated: false,
        consumer: null,
        isConsumer: false,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    const { unsubscribe } = subscribeToAuthChanges((event, session) => {
      const { orgId, orgRole } = extractOrgContext(session);

      // Detect email confirmation transition: null -> confirmed
      const currentConfirmed = session?.user?.email_confirmed_at ?? null;
      if (previousEmailConfirmedAt.current === null && currentConfirmed) {
        try {
          getPosthog()?.capture('email_confirmed');
        } catch { /* silent */ }
      }
      previousEmailConfirmedAt.current = currentConfirmed;

      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        organizationId: orgId,
        orgRole,
        isLoading: session?.user ? true : prev.isLoading,
      }));

      if (session?.user) {
        setTimeout(() => {
          fetchUserData(session.user.id, session);
        }, 0);
      } else {
        previousEmailConfirmedAt.current = undefined;
        setState(prev => ({
          ...prev,
          profile: null,
          role: null,
          orgRole: null,
          organizationId: null,
          isAdmin: false,
          isOwner: false,
          isAtendente: false,
          isPrestador: false,
          organization: null,
          membership: null,
          needsOnboarding: false,
          accountDeactivated: false,
          consumer: null,
          isConsumer: false,
          isLoading: false,
        }));
      }
    });

    getCurrentSession().then((session) => {
      const { orgId, orgRole } = extractOrgContext(session);

      // Seed the ref with the initial email_confirmed_at so later transitions are detected correctly
      const currentConfirmed = session?.user?.email_confirmed_at ?? null;
      if (previousEmailConfirmedAt.current === undefined) {
        previousEmailConfirmedAt.current = currentConfirmed;
      }

      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        organizationId: orgId,
        orgRole,
      }));

      if (session?.user) {
        fetchUserData(session.user.id, session);
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    });

    return () => unsubscribe();
  }, [fetchUserData]);

  const signIn = async (email: string, password: string) => {
    return signInService({ email, password });
  };

  const signUp = async (email: string, password: string, nome: string) => {
    const redirectUrl = `${window.location.origin}/`;
    return signUpService({ email, password, nome, redirectUrl });
  };

  const signOut = async () => {
    return signOutService();
  };

  const refreshOrganization = useCallback(async () => {
    const orgId = currentOrgIdRef.current;
    if (!orgId) return;
    const organization = await getOrganizationById(orgId);
    setState(prev => ({ ...prev, organization }));
  }, []);

  const refreshProfile = useCallback(async () => {
    const userId = currentUserIdRef.current;
    if (!userId) return;
    try {
      const profile = await getProfileByUserId(userId);
      setState(prev => ({ ...prev, profile }));
    } catch (error) {
      console.error('Erro ao re-carregar perfil:', error);
    }
  }, []);

  const refreshConsumer = useCallback(async () => {
    const userId = currentUserIdRef.current;
    if (!userId) return;
    try {
      const consumer = await getConsumerById(userId);
      setState(prev => ({ ...prev, consumer, isConsumer: consumer !== null }));
    } catch (error) {
      console.error('Erro ao re-carregar consumer:', error);
    }
  }, []);

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    refreshOrganization,
    refreshProfile,
    refreshConsumer,
  };
}
